import * as React from "react";
import { useSegments } from '#app/lib/hooks/router-compat';
import { useWorkspace } from "#app/lib/data/workspace-context";

import {
  useNavPreferences,
  type ResolvedRailTile,
} from "#app/lib/hooks/use-nav-preferences";
import type { HomeSectionId } from "#app/components/home/home-sections";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { tileIdForRouteSegment, tileVisibleToViewer, TILES } from "./tiles";

/**
 * ModuleRailContext — shared state for the far-left ModuleRail.
 *
 * The active tile is DERIVED from the current route (the route is the source
 * of truth), so it matches on reload and back/forward navigation; rail
 * presses just navigate. The rail and the workspace layout (which resolves
 * the module menu in the sidebar pane) live in separate subtrees of the shell,
 * so the value is shared via context here. Mounted in `app-providers.tsx`
 * above `BottomSheetModalProvider` per the provider rules in the app
 * AGENTS.md.
 *
 * Nav preferences (compact mode, tile order/visibility, Home section order)
 * come from a SINGLE `useNavPreferences` instance owned by this provider —
 * its optimistic override state is per-instance, so consumers (the rail, the
 * Customize Navigation dialog, the Home module menu) must read through this
 * context rather than calling the hook themselves, or their edits wouldn't
 * reflect elsewhere until the server round-trip lands.
 */

export type ModuleRailTileId = string;

/**
 * The compact drawer's one browse target that is not a module tile: settings'
 * own navigation pane. Settings has no rail tile (`activeTileId` is null on
 * its routes), so the drawer needs a name for "show the settings pane" that
 * is independent of the route — entering settings from an open drawer swaps
 * the pane to it without navigating, the mirror of "Back to app" swapping to
 * a module's menu. Never a `TILES` id.
 */
export const SETTINGS_BROWSE_ID = "settings";

type ModuleRailContextValue = {
  /** Derived from the current route. Null when no module owns the route
   * (e.g. settings, which keeps its own dedicated sidebar) — no tile
   * highlights, no module menu renders. */
  activeTileId: ModuleRailTileId | null;
  /**
   * Compact-drawer browsing: the module whose menu the drawer is SHOWING,
   * decoupled from the route. A strip press sets it without navigating — the
   * user is choosing where to go, and yanking the page (Home's root redirect
   * lands on the default view) before they pick a row closed the drawer out
   * from under them. Null = follow the route. The workspace layout resolves
   * the pane menu from `browseTileId ?? activeTileId` and clears it when the
   * drawer closes or a navigation lands. `SETTINGS_BROWSE_ID` names the
   * settings pane, which is not a tile.
   */
  browseTileId: ModuleRailTileId | null;
  setBrowseTileId: (tileId: ModuleRailTileId | null) => void;
  /**
   * Compact mode: rail tiles render icon-only and the rail column narrows.
   * The workspace layout reads it for the AppShell's `moduleRailWidth`; the
   * Customize Navigation dialog toggles it.
   */
  compact: boolean;
  setCompact: (compact: boolean) => void;
  /** Home's unread count rides its tile as a bare dot, not the number. */
  countDot: boolean;
  /** User-ordered tile list resolved against the registry — includes hidden
   * (`show: false`) tiles; the rail renders the shown ones, the More popup
   * lists the hidden ones. Already narrowed to the viewer's tier: a Guest's
   * list has no member modules (`tileVisibleToViewer`), so the rail, the
   * compact strip, the More popup and the Customize dialog agree without
   * each asking. */
  railTiles: ResolvedRailTile[];
  setRailTiles: (tiles: ResolvedRailTile[]) => void;
  /** User-ordered Home module-menu section ids (the pinned "nav" section is
   * never in the list — `HomeModuleMenu` prepends it). */
  homeSectionOrder: HomeSectionId[];
  setHomeSectionOrder: (order: string[]) => void;
  /** Sidebar workflow-list scope toggles (the Workflows pane's "Workflow
   * settings" menu); the workflow section filters on them. Both off by
   * default. */
  showAllWorkflows: boolean;
  setShowAllWorkflows: (show: boolean) => void;
  showArchivedWorkflows: boolean;
  setShowArchivedWorkflows: (show: boolean) => void;
};

const ModuleRailContext = React.createContext<ModuleRailContextValue | null>(
  null,
);

export function ModuleRailProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Derive the active tile from the route segment after the workspace slug.
  // Outside a workspace route (login, onboarding) no tile is active — the
  // rail isn't rendered there anyway.
  const segments = useSegments() as string[];
  // Pathnames carry the real workspace slug (e.g. "sales/members") — locate the
  // workspace segment by its actual urlPath, then the next segment is the
  // active module.
  const { activeWorkspace } = useWorkspace();
  const workspaceIndex = activeWorkspace?.urlPath
    ? segments.indexOf(activeWorkspace.urlPath)
    : -1;
  const moduleSegment =
    workspaceIndex === -1 ? undefined : segments[workspaceIndex + 1];
  const routeTileId =
    workspaceIndex === -1 ? null : tileIdForRouteSegment(moduleSegment);
  const viewerIsShareOnly = useViewerIsShareOnly();
  // A route owned by a module the viewer cannot see highlights no tile — with
  // one exception. A shared workflow's board is a share-only viewer's
  // destination even though the Workflows module is not theirs: it reached
  // them through Home's Workflows section, so Home stays lit and Home's menu
  // stays up while they are on it. Every other member-module route is denied
  // by its guard, and an unlit rail over "You do not have access" is the
  // honest state.
  const routeTile = TILES.find((tile) => tile.id === routeTileId);
  const activeTileId =
    routeTile && !tileVisibleToViewer(routeTile, viewerIsShareOnly)
      ? moduleSegment === "workflow"
        ? "home"
        : null
      : routeTileId;
  const [browseTileId, setBrowseTileId] =
    React.useState<ModuleRailTileId | null>(null);

  const {
    compact,
    setCompact,
    countDot,
    railTiles: allRailTiles,
    setRailTiles,
    homeSectionOrder,
    setHomeSectionOrder,
    showAllWorkflows,
    setShowAllWorkflows,
    showArchivedWorkflows,
    setShowArchivedWorkflows,
  } = useNavPreferences();

  // The preference keeps every tile — a share-only viewer promoted to Member
  // tomorrow gets their saved order back intact — and only the list handed
  // out is narrowed.
  // A write from the Customize dialog carries the narrowed list; the resolver
  // slots the tiles it lacks back in at their registry position, shown.
  const railTiles = React.useMemo(
    () =>
      allRailTiles.filter((tile) =>
        tileVisibleToViewer(tile, viewerIsShareOnly),
      ),
    [allRailTiles, viewerIsShareOnly],
  );

  const value = React.useMemo(
    () => ({
      activeTileId,
      browseTileId,
      setBrowseTileId,
      compact,
      setCompact,
      countDot,
      railTiles,
      setRailTiles,
      homeSectionOrder,
      setHomeSectionOrder,
      showAllWorkflows,
      setShowAllWorkflows,
      showArchivedWorkflows,
      setShowArchivedWorkflows,
    }),
    [
      activeTileId,
      browseTileId,
      compact,
      setCompact,
      countDot,
      railTiles,
      setRailTiles,
      homeSectionOrder,
      setHomeSectionOrder,
      showAllWorkflows,
      setShowAllWorkflows,
      showArchivedWorkflows,
      setShowArchivedWorkflows,
    ],
  );

  return (
    <ModuleRailContext.Provider value={value}>
      {children}
    </ModuleRailContext.Provider>
  );
}

export function useModuleRail(): ModuleRailContextValue {
  const ctx = React.useContext(ModuleRailContext);
  if (!ctx) {
    throw new Error("useModuleRail must be used within a ModuleRailProvider");
  }
  return ctx;
}
